// B2B / Detail firmy (schválený mockup 3): karta firmy se záložkami, Osoby a Údaje.
// Záložky: Časová osa, Akce (fáze 3a), Komentáře. Tlačítka Napsat e-mail a Nová nabídka přijdou s fází 4.
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Building2, ChevronLeft, Pencil, CalendarClock, Archive, ArchiveRestore, Ban, ArrowRightLeft, Phone, Mail, Users, Info, Globe, MapPin,
  Send, Reply, Handshake, FileText, MessageSquare, Inbox, UserPlus, CalendarCheck, Plus,
} from "lucide-react";
import Card from "../../components/Card.jsx";
import { Stranka, Zalozky, NabidkaAkci, ErrorNote, SkeletonRows } from "../../components/ui.jsx";
import { Pil, KartaLista, RadekShrnuti } from "../../components/sales/salesUi.jsx";
import KomentareKontaktu from "../../components/sales/KomentareKontaktu.jsx";
import { useUzkaObrazovka } from "../../lib/obrazovka.js";
import { ton as tonPodleKlice } from "../../lib/tony.js";
import { toast } from "../../components/Toast.jsx";
import { confirmDialog } from "../../components/confirmHost.jsx";
import { kdyUdalost } from "../../components/sales/salesApi.js";
import { b2bGet, b2bPost } from "../../components/b2b/b2bApi.js";
import { StavPil, SegPil, KontaktRadek, ZDROJE, krokPil, dmr, webHref, telHref, StavAkcePil, kc, terminText } from "../../components/b2b/b2bUi.jsx";
import { FirmaDialog, StavDialog, KrokDialog, OsobaDialog } from "../../components/b2b/FirmaDialogy.jsx";
import { AkceDialog } from "../../components/b2b/AkceDialogy.jsx";

const IKONY_OSY = {
  zmena_stavu: [ArrowRightLeft, "neutralni", "Změna stavu"],
  email_odeslan: [Send, "neutralni", "E-mail odeslán"],
  followup_odeslan: [Send, "pozor", "Follow-up odeslán"],
  odpoved: [Reply, "info", "Odpověď"],
  hovor: [Phone, "neutralni", "Hovor"],
  schuzka: [Handshake, "ok", "Schůzka"],
  nabidka: [FileText, "znacka", "Nabídka"],
  poptavka: [Inbox, "problem", "Poptávka"],
  poznamka: [MessageSquare, "neutralni", "Záznam"],
  akce: [CalendarCheck, "ok", "Akce"],
};

function CasovaOsa({ aktivity }) {
  if (!aktivity.length) return <p className="text-sm text-slate-500">Zatím tu nic není. Každá změna stavu a další události se zapisují sem.</p>;
  return (
    <ol className="space-y-0">
      {aktivity.map((a, i) => {
        const [Ikona, ton, popis] = IKONY_OSY[a.typ] || IKONY_OSY.poznamka;
        const t = tonPodleKlice(ton);
        const k = kdyUdalost(a.kdy);
        return (
          <li key={a.id} className="relative flex gap-3 pb-4 last:pb-0">
            {i < aktivity.length - 1 && <span aria-hidden className="absolute bottom-0 left-[17px] top-9 w-px bg-slate-200" />}
            <span className={`relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${t.pole}`}><Ikona className="h-4 w-4" /></span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <Pil ton={ton} mala casti={popis} />
                <span className="text-xs tabular-nums text-slate-500">{[k.den, k.cas].filter(Boolean).join(" ")}</span>
                {a.autor_name && <span className="text-xs text-slate-500">{a.autor_name}</span>}
              </div>
              {a.text && <div className="mt-1 text-[15px] leading-snug text-slate-800">{a.text}</div>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function AkceFirmy({ akce, onNova, onOtevrit }) {
  return (
    <div className="space-y-3">
      <button type="button" className="btn-secondary" onClick={onNova}><Plus className="h-4 w-4" />Nová akce</button>
      {akce === null && <p className="text-sm text-slate-500">Načítám akce.</p>}
      {akce && akce.length === 0 && <p className="text-sm text-slate-500">Firma zatím nemá žádnou akci. Po prvním potvrzení se z ní sama stane klient.</p>}
      {akce && akce.map((a) => {
        const t = terminText(a);
        return (
          <button key={a.id} type="button" onClick={() => onOtevrit(a.id)} className="polozka block min-h-[44px] w-full space-y-1.5 text-left">
            <div className="text-base font-bold leading-snug text-slate-900">{a.nazev}</div>
            <div className="flex flex-wrap gap-1.5">
              <StavAkcePil stav={a.stav} />
              <Pil ton="neutralni" ikona={CalendarCheck} casti={[t.datum, t.cas].filter(Boolean)} />
              {a.cena_bez_dph != null && <Pil ton="neutralni" casti={kc(a.cena_bez_dph)} />}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function Osoby({ osoby, onPridat, onUpravit }) {
  return (
    <Card title="Osoby" ikona={Users} odznak={osoby.length} action={<button type="button" className="btn-secondary btn-sm" onClick={onPridat}><UserPlus className="h-4 w-4" />Přidat osobu</button>}>
      {osoby.length === 0 && <p className="text-sm text-slate-500">Zatím tu není žádný kontakt. Bez osoby se nedá psát e-mail ani volat.</p>}
      <div className="space-y-3">
        {osoby.map((o) => (
          <div key={o.id} className="polozka space-y-2">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-base font-bold text-slate-900">{o.jmeno}</span>
              {o.hlavni && <Pil ton="znacka" mala casti="hlavní kontakt" />}
              {o.nekontaktovat && <Pil ton="problem" mala casti="nekontaktovat" />}
              <button type="button" className="btn-ikona ml-auto" aria-label={`Upravit osobu ${o.jmeno}`} title="Upravit" onClick={() => onUpravit(o)}><Pencil className="h-4 w-4" /></button>
            </div>
            {o.role && <div className="text-[13px] text-slate-600">{o.role}</div>}
            <div>
              {o.telefon && <KontaktRadek ikona={Phone} href={telHref(o.telefon)} hodnota={o.telefon} kopie="telefon" />}
              {o.email && <KontaktRadek ikona={Mail} href={`mailto:${o.email}`} hodnota={o.email} kopie="e-mail" />}
            </div>
            {(o.telefon || o.email) && !o.nekontaktovat && (
              <div className="flex items-center gap-2">
                {o.telefon && <a href={telHref(o.telefon)} className="btn-secondary btn-sm flex-1"><Phone className="h-4 w-4" />Zavolat</a>}
                {o.email && <a href={`mailto:${o.email}`} className="btn-secondary btn-sm flex-1"><Mail className="h-4 w-4" />Napsat</a>}
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

function Udaje({ f, obchodnik, onUpravit }) {
  return (
    <Card title="Údaje" ikona={Info} action={<button type="button" className="btn-secondary btn-sm" onClick={onUpravit}><Pencil className="h-4 w-4" />Upravit</button>}>
      <div>
        <RadekShrnuti popisek="IČO">{f.ico || "neuvedeno"}</RadekShrnuti>
        <RadekShrnuti popisek="DIČ">{f.dic || "neuvedeno"}</RadekShrnuti>
        <RadekShrnuti popisek="Adresa">{f.adresa || "neuvedeno"}</RadekShrnuti>
        <RadekShrnuti popisek="Web">{f.web ? <KontaktRadek ikona={Globe} href={webHref(f.web)} hodnota={f.web} kopie="web" externi /> : "neuvedeno"}</RadekShrnuti>
        <RadekShrnuti popisek="Telefon firmy">{f.telefon ? <KontaktRadek ikona={Phone} href={telHref(f.telefon)} hodnota={f.telefon} kopie="telefon" /> : "neuvedeno"}</RadekShrnuti>
        <RadekShrnuti popisek="E-mail firmy">{f.email ? <KontaktRadek ikona={Mail} href={`mailto:${f.email}`} hodnota={f.email} kopie="e-mail" /> : "neuvedeno"}</RadekShrnuti>
        <RadekShrnuti popisek="Velikost">{f.velikost ? `${f.velikost} zaměstnanců` : "neuvedeno"}</RadekShrnuti>
        <RadekShrnuti popisek="Zdroj">{ZDROJE[f.zdroj] || f.zdroj}</RadekShrnuti>
        <RadekShrnuti popisek="Obchodník od">{f.obchodnik_id ? `${obchodnik}${f.prirazeno_at ? `, ${dmr(f.prirazeno_at)}` : ""}` : "bez obchodníka"}</RadekShrnuti>
      </div>
      {f.poznamka && <p className="mt-2 whitespace-pre-line text-sm text-slate-700">{f.poznamka}</p>}
      <p className="mt-2 text-xs text-slate-500">Po zadání IČO se název, adresa a DIČ dotáhnou z veřejného ARES.</p>
    </Card>
  );
}

export default function DetailFirmy() {
  const { id } = useParams();
  const navigate = useNavigate();
  const uzka = useUzkaObrazovka();
  const [d, setD] = useState(null);
  const [obchodnici, setObchodnici] = useState([]);
  const [chyba, setChyba] = useState("");
  const [zalozka, setZalozka] = useState("osa");
  const [okno, setOkno] = useState(null); // { typ: "upravit" | "stav" | "krok" | "osoba" | "akce", ... }
  const [akceFirmy, setAkceFirmy] = useState(null);

  const nacti = useCallback(async () => {
    try { setD(await b2bGet("firma", { id })); setChyba(""); }
    catch (e) { setChyba(e.message); }
  }, [id]);
  useEffect(() => { setD(null); nacti(); }, [nacti]);
  const nactiAkce = useCallback(() => { b2bGet("akce_firmy", { firma: id }).then((r) => setAkceFirmy(r.akce || [])).catch(() => setAkceFirmy([])); }, [id]);
  useEffect(() => { setAkceFirmy(null); nactiAkce(); }, [nactiAkce]);
  useEffect(() => { b2bGet("obchodnici").then((r) => setObchodnici(r.obchodnici || [])).catch(() => {}); }, []);

  const zpet = (
    <div><button type="button" className="btn-secondary" onClick={() => navigate("/b2b/firmy")}><ChevronLeft className="h-4 w-4" />Firmy</button></div>
  );
  if (chyba) return <Stranka sirka="plna">{zpet}<ErrorNote onRetry={nacti}>{chyba}</ErrorNote></Stranka>;
  if (!d) return <Stranka sirka="plna">{zpet}<SkeletonRows rows={5} /></Stranka>;

  const f = d.firma;
  const obchodnik = obchodnici.find((o) => o.email === f.obchodnik_id)?.jmeno || f.obchodnik_id;
  const krok = krokPil(f);
  const zavri = () => setOkno(null);
  const poZmene = () => { zavri(); nacti(); };

  const nekontaktovat = async () => {
    const ok = await confirmDialog({ title: "Nekontaktovat firmu?", description: `${f.nazev} se natvrdo vyřadí z oslovování a z fronty Dnes. Změna se zapíše do časové osy.`, confirmLabel: "Nekontaktovat", variant: "danger" });
    if (!ok) return;
    try { await b2bPost("stav", { id: f.id, stav: "nekontaktovat" }); toast("Firma je označená jako nekontaktovat.", "success"); nacti(); }
    catch (e) { toast(e.message, "error"); }
  };
  const archivovat = async () => {
    const ok = await confirmDialog({
      title: f.archived ? "Obnovit firmu z archivu?" : "Archivovat firmu?",
      description: f.archived ? `${f.nazev} se znovu objeví v seznamu firem.` : `${f.nazev} zmizí ze seznamu firem a z fronty Dnes. Dá se obnovit.`,
      confirmLabel: f.archived ? "Obnovit" : "Archivovat", variant: "primary",
    });
    if (!ok) return;
    try { await b2bPost("archivovat", { id: f.id, archived: !f.archived }); toast(f.archived ? "Firma obnovena." : "Firma archivována.", "success"); nacti(); }
    catch (e) { toast(e.message, "error"); }
  };

  const menu = [
    { klic: "upravit", popis: "Upravit údaje", ikona: Pencil, onClick: () => setOkno({ typ: "upravit" }) },
    { klic: "stav", popis: "Změnit stav", ikona: ArrowRightLeft, onClick: () => setOkno({ typ: "stav" }) },
    { klic: "odlozit", popis: "Odložit firmu", ikona: CalendarClock, onClick: () => setOkno({ typ: "stav", vychozi: "odlozeno" }) },
    { klic: "archiv", popis: f.archived ? "Obnovit z archivu" : "Archivovat", ikona: f.archived ? ArchiveRestore : Archive, onClick: archivovat },
    ...(f.stav !== "nekontaktovat" ? [{ klic: "nk", popis: "Nekontaktovat", ikona: Ban, onClick: nekontaktovat, nebezpecna: true, oddelit: true }] : []),
  ];

  const hlavni = (
    <KartaLista ikona={Building2} nazev={f.nazev} akce={<NabidkaAkci polozky={menu} tmava popis="Další akce" />}>
      <div className="space-y-4">
        <div className="flex flex-wrap gap-1.5">
          <StavPil stav={f.stav} />
          <SegPil segment={f.segment} />
          <Pil ton="neutralni" casti={["obchodník", obchodnik || "bez obchodníka"]} />
          {f.mesto && <Pil ton="neutralni" ikona={MapPin} casti={f.mesto} />}
          {f.archived && <Pil ton="pozor" casti="archivováno" />}
          {f.stav === "odlozeno" && f.odlozeno_do && <Pil ton="pozor" casti={["vrátí se", dmr(f.odlozeno_do)]} />}
          {f.stav === "ztraceno" && f.duvod_ztraty && <Pil ton="neutralni" casti={["důvod", f.duvod_ztraty]} />}
        </div>

        <div className="rounded-2xl border border-brand-200 bg-brand-50 p-3 sm:p-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-brand-deep">Další krok</span>
            {krok && <Pil ton={krok.ton} mala casti={f.dalsi_krok_datum ? `${dmr(f.dalsi_krok_datum)}${f.dalsi_krok_cas ? ` ${f.dalsi_krok_cas}` : ""}` : krok.text} />}
          </div>
          <div className="mt-1 text-base font-bold text-slate-900">{f.dalsi_krok || "Další krok není určený"}</div>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            <button type="button" className="btn-secondary" onClick={() => setOkno({ typ: "krok" })}><CalendarClock className="h-4 w-4" />{f.dalsi_krok ? "Změnit další krok" : "Určit další krok"}</button>
          </div>
        </div>

        <Zalozky aria-label="Obsah firmy" value={zalozka} onChange={setZalozka} stitek="Obsah"
          items={[{ key: "osa", label: "Časová osa" }, { key: "akce", label: "Akce" }, { key: "komentare", label: "Komentáře" }]} />
        {zalozka === "osa" && <CasovaOsa aktivity={d.aktivity} />}
        {zalozka === "akce" && <AkceFirmy akce={akceFirmy} onNova={() => setOkno({ typ: "akce" })} onOtevrit={(aid) => navigate(`/b2b/akce/${aid}`)} />}
        {zalozka === "komentare" && <KomentareKontaktu leadId={f.id} initial={d.komentare} post={b2bPost} onChanged={nacti} />}
      </div>
    </KartaLista>
  );

  const osoby = <Osoby osoby={d.osoby} onPridat={() => setOkno({ typ: "osoba" })} onUpravit={(o) => setOkno({ typ: "osoba", osoba: o })} />;
  const udaje = <Udaje f={f} obchodnik={obchodnik} onUpravit={() => setOkno({ typ: "upravit" })} />;

  return (
    <Stranka sirka="plna">
      {zpet}
      {uzka ? (
        <div className="space-y-4">{hlavni}{osoby}{udaje}</div>
      ) : (
        <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_390px]">
          {hlavni}
          <div className="space-y-4">{osoby}{udaje}</div>
        </div>
      )}
      <FirmaDialog open={okno?.typ === "upravit"} firma={f} onClose={zavri} onHotovo={poZmene} />
      <StavDialog open={okno?.typ === "stav"} firma={f} vychozi={okno?.vychozi} onClose={zavri} onHotovo={poZmene} />
      <KrokDialog open={okno?.typ === "krok"} firma={f} onClose={zavri} onHotovo={poZmene} />
      <AkceDialog open={okno?.typ === "akce"} firma={f} osoby={d.osoby} onClose={zavri} onHotovo={(a) => { zavri(); navigate(`/b2b/akce/${a.id}`); }} />
      <OsobaDialog open={okno?.typ === "osoba"} firma={f} osoba={okno?.osoba} onClose={zavri} onHotovo={poZmene} />
    </Stranka>
  );
}
